import classNames from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { promotionEndDate } from 'reactApp/appHelpers/featuresHelpers';
import { mayPromo2025YearProductId } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import { Button } from 'reactApp/ui/Button/Button';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

import styles from './PromoCounter.css';
import { usePromoCountdown } from './usePromoCountdown';

export function PromoCounter({
    isIOSPromo = false,
    isAndroidPromo = false,
    isMobilePayAvailable,
    mayPromo2025Enabled,
    isCompact = false,
}: {
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isMobilePayAvailable?: boolean;
    mayPromo2025Enabled?: boolean;
    isCompact?: boolean;
}) {
    const dispatch = useDispatch();

    const { daysLeft, isPromoActive, daysLeftText } = usePromoCountdown(promotionEndDate);

    const { isLoading, isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const productId = mayPromo2025YearProductId;

    useEffect(() => {
        if (mayPromo2025Enabled && productId) {
            sendPaymentGa({
                eventCategory: ECategoryGa.button,
                action: 'show',
                name: 'mnz_promo_action_ms_june25_landing',
            });
            emitAnalyticEvent(AnalyticEventNames.MAY_PROMO_2025_BUY_TARIFF_SHOW);
        }
    }, []);

    const onBuyClick = useCallback(() => {
        if (productId) {
            if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
                revealPlatformPopup(productId, isIOSPromo, isAndroidPromo);
                return;
            }

            sendPaymentGa({
                eventCategory: ECategoryGa.button,
                action: 'click',
                paySource: 'action_button1tb',
                name: 'mnz_promo_action_ms_june25_landing',
            });
            emitAnalyticEvent(AnalyticEventNames.MAY_PROMO_2025_BUY_TARIFF_CLICK);
            sendTmrGoal({ id: TMR3473039, goal: 'click-trial-button' });

            dispatch(
                openTariffBuy({
                    productId,
                    checkAnonymous: true,
                    isMobile,
                    paySource: getPaySource('action_button1tb'),
                    hideOtherTariffs: true,
                })
            );
        }
    }, [dispatch, isAndroidPromo, isIOSPromo, isMobile, isMobilePayAvailable, productId]);

    if (!isPromoActive || !mayPromo2025Enabled) {
        return null;
    }

    return (
        <div
            className={classNames(styles.root, {
                [styles.rootMobile]: isMobile,
                [styles.rootMayPromo2025]: mayPromo2025Enabled,
                [styles.rootCompact]: isCompact,
            })}
        >
            <div className={styles.contentWrapper}>
                <div className={styles.textWrapper}>
                    {daysLeft === 0 ? (
                        <>
                            Акция закончится{isMobile && <>&nbsp;</>}
                            <div className={styles.root_days}>сегодня</div>
                        </>
                    ) : isCompact ? (
                        <>
                            {'Ещё'}
                            {isMobile && <>&nbsp;</>}
                            <div className={styles.root_days}>{daysLeftText}</div>
                            {isMobile && <>&nbsp;</>}
                            {'акции'}
                        </>
                    ) : (
                        <>
                            {'Акция закончится через'}
                            {isMobile && <>&nbsp;</>}
                            <div className={styles.root_days}>{daysLeftText}</div>
                        </>
                    )}
                </div>
                {mayPromo2025Enabled && productId && (
                    <Button className={styles.buyTariffButton} primary theme="vk" onClick={onBuyClick} disabled={isLoading || !isLoaded}>
                        {isCompact ? '1 ТБ за 0 ₽' : '1 ТБ на 3 месяца бесплатно'}
                    </Button>
                )}
            </div>
        </div>
    );
}
